import React, { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import useResizeObserver from "@dineout-cart-client/hooks/useResizeObserver";

// deps
import { TurboPayment } from "@swiggy-private/payment-client";
import { currencyFormat, INR_CURRENCY_SYMBOL_TEXT } from "@swiggy-private/dineout-payment-success-ui";
import { DineoutCartContext, NEWRELIC_TABLES, recordNREvent, goToHome } from "@swiggy-private/checkout-core";
import { isTurboEnabled } from "@dineout-cart-client/helpers/TurboPayment";

// hooks
import useDineoutCartActions from "@dineout-cart-client/hooks/useDineoutCartActions";

import { ErrorWidget } from "@swiggy-private/core-ui";

// helpers
import { getStealDealAvailableCartItems } from "@dineout-cart-client/helpers/StealDealCart";

// constants
import { PROCEED_TOPAY_TEXT } from "@dineout-cart-client/constants";
import { CART_ERROR_CTA, CART_STATUS_CODES } from "@swiggy-private/cart-types";

//styles
import { ButtonTextContainer, CTAContainer, ProceedCTA, StyledText } from "../payment-cart/styles";
import { FooterContainer } from "./styles";
import { format } from "@swiggy-private/js-utils";
import { getErrorWidgetAttrs } from "@dineout-cart-client/helpers/Error";
import { CartFooterProps } from "./types";
import { isStealStoreCart } from "@dineout-cart-client/helpers/cartHelper";

const PAGE_DO_WEBVIEW = "do-webview";

const CartFooter = ({
    uniqueIdForCart,
    billToPay,
    flowtype = "",
    restaurantID = 0,
    onRemoveUnavailableItems,
    onRemoveExcessItems,
    onClearCart,
    onDismissPaymentAlert,
    onHeightChange,
    analytics: {
        onClickTurboPaymentMethodChange,
        onImpressionTurboCheckout,
        onImpressionTurboPayBtn,
        onClickTurboCheckout,
    },
}: CartFooterProps) => {
    const [isTurboPayWidgetHidden, setIsTurboPayWidgetHidden] = useState(false);
    const [isSoftErrorBottomSheetDismissed, setIsSoftErrorBottomSheetDismissed] = useState(false);
    const footerRef = useRef<HTMLDivElement>(null);
    const { cartState, cartType } = useContext(DineoutCartContext);
    const { reviewedCart } = cartState;
    const cartResponse = useMemo(() => reviewedCart?.data, [reviewedCart]);

    useEffect(() => {
        setIsSoftErrorBottomSheetDismissed(false);
    }, [reviewedCart]);

    useResizeObserver(footerRef, onHeightChange);

    const turboEventPayload = useMemo(
        () => ({
            restaurantID,
            sourceScreenValue: "",
            flow: flowtype,
            page: PAGE_DO_WEBVIEW,
            buttonVariant: "button",
        }),
        [restaurantID, flowtype],
    );

    const proceedToPayText = useMemo(() => {
        return `${PROCEED_TOPAY_TEXT} ${format(INR_CURRENCY_SYMBOL_TEXT, currencyFormat(billToPay || 0) || "0")}`;
    }, [billToPay]);

    const { onClickProceed: onProceedToPay } = useDineoutCartActions({
        skipPayment: cartResponse?.bill?.skipPayment,
        skipValidation: isStealStoreCart(cartType),
        skipPaymentLink: isStealStoreCart(cartType),
    });

    const reviewedCartMeta = useMemo(
        () => ({
            statusCode: reviewedCart?.statusCode,
            statusMessage: reviewedCart?.statusMessage,
            ctaAction: reviewedCart?.ctaAction,
            ctaText: reviewedCart?.ctaText,
            statusTitle: reviewedCart?.statusTitle,
            secondaryCtaText: reviewedCart?.secondaryCtaText,
            secondaryCtaAction: reviewedCart?.secondaryCtaAction,
        }),
        [reviewedCart],
    );

    const hasCartError = useMemo(() => {
        const { statusCode } = reviewedCartMeta;

        return statusCode != null && statusCode !== CART_STATUS_CODES.SUCCESS && !isSoftErrorBottomSheetDismissed;
    }, [reviewedCartMeta, isSoftErrorBottomSheetDismissed]);

    const _isTurboEnabled = useMemo(() => isTurboEnabled(), []);
    const onErrorTurboPay = useCallback(() => {
        setIsTurboPayWidgetHidden(true);
        recordNREvent({
            eventType: NEWRELIC_TABLES.TURBO_PAY_WIDGET_HIDDEN_DUE_TO_ERROR,
            isTurboEnabled: String(_isTurboEnabled),
        });
    }, [_isTurboEnabled]);

    const onPayClickTurboCheckout = useCallback(
        (_paymentMethod: string, _billAmount?: string, groupName?: string) => {
            if (_isTurboEnabled) {
                onClickTurboCheckout({
                    ...turboEventPayload,
                    paymentType: groupName,
                    billTotal: _billAmount,
                    paymentMethod: _paymentMethod,
                });
            }
        },
        [onClickTurboCheckout, _isTurboEnabled, turboEventPayload],
    );

    const onPayClickTurboPaymentMethodChange = useCallback(
        (_paymentMethod: string, _billAmount: string, _fromFallback?: boolean, groupName?: string) => {
            if (_isTurboEnabled) {
                onClickTurboPaymentMethodChange({ ...turboEventPayload, paymentType: groupName });
            }
        },
        [onClickTurboPaymentMethodChange, _isTurboEnabled, turboEventPayload],
    );

    const handleTurboCheckoutImpression = useCallback(
        (_paymentMethod: string, _billAmount: string, _fromFallback?: boolean, groupName?: string) => {
            if (_isTurboEnabled) {
                onImpressionTurboCheckout({ ...turboEventPayload, paymentType: groupName });
            }
        },
        [onImpressionTurboCheckout, _isTurboEnabled, turboEventPayload],
    );

    const handleTurboPayBtnImpression = useCallback(
        (_paymentMethod: string, _billAmount: string, groupName?: string) => {
            if (_isTurboEnabled) {
                onImpressionTurboPayBtn({ ...turboEventPayload, paymentType: groupName });
            }
        },
        [onImpressionTurboPayBtn, _isTurboEnabled, turboEventPayload],
    );

    const reloadCart = useCallback(() => {
        window.location.reload();
    }, []);

    const { unavailableItems } = useMemo(
        () => getStealDealAvailableCartItems(cartResponse?.groupedItems),
        [cartResponse?.groupedItems],
    );

    const onErrorCtaAction = useCallback(
        async (action: string) => {
            switch (action.toLowerCase()) {
                case CART_ERROR_CTA.CLEAR_CART:
                    onClearCart();
                    break;

                case CART_ERROR_CTA.REMOVE_UNAVAILABLE_ITEMS:
                case CART_ERROR_CTA.REMOVE_UNSERVICEABLE_ITEMS:
                    onRemoveUnavailableItems();
                    break;
                case CART_ERROR_CTA.REMOVE_EXCESS_ITEMS:
                    onRemoveExcessItems();
                    break;
                case CART_ERROR_CTA.KEEP_ALL_ITEMS:
                    setIsSoftErrorBottomSheetDismissed(true);
                    break;
                case CART_ERROR_CTA.GO_TO_HOME:
                    goToHome();
                    break;
                case CART_ERROR_CTA.RETRY:
                default:
                    reloadCart();
                    break;
            }
        },
        [reloadCart, onRemoveUnavailableItems, onRemoveExcessItems, setIsSoftErrorBottomSheetDismissed, onClearCart],
    );

    const handlePaymentAlertPrimaryAction = useCallback(() => {
        onRemoveExcessItems();
        // we need to dismiss the payment alert so that the next error dispatch
        // causes GenericPaymentAlert to unmount and remount fresh (BottomSheet resets its
        // internal visibility state).
        onDismissPaymentAlert?.();
    }, [onRemoveExcessItems, onDismissPaymentAlert]);

    const renderContent = useCallback(() => {
        if (hasCartError) {
            return (
                <ErrorWidget
                    action={onErrorCtaAction}
                    statusCode={reviewedCartMeta?.statusCode || 0}
                    statusMessage={reviewedCartMeta?.statusMessage || ""}
                    ctaText={reviewedCartMeta?.ctaText || ""}
                    ctaAction={reviewedCartMeta.ctaAction as CART_ERROR_CTA}
                    unServiceableItemsCount={unavailableItems.length}
                    getErrorWidgetAttrs={getErrorWidgetAttrs}
                    titleMessage={reviewedCartMeta?.statusTitle || ""}
                    secondaryCtaAction={reviewedCartMeta?.secondaryCtaAction}
                    secondaryCtaText={reviewedCartMeta?.secondaryCtaText || ""}
                />
            );
        }

        return (
            <CTAContainer showTurboPayment={_isTurboEnabled && !isTurboPayWidgetHidden}>
                {_isTurboEnabled && !isTurboPayWidgetHidden && uniqueIdForCart && (
                    <TurboPayment
                        uniqueId={uniqueIdForCart}
                        onPaymentMethodChange={onProceedToPay}
                        onError={onErrorTurboPay}
                        showLoader={false}
                        showDiscountedValueOnSlider={false}
                        isTurboV2={true}
                        hideEducationComms={false}
                        onTurboPayClickEvent={onPayClickTurboCheckout}
                        onPaymentMethodChangeClickEvent={onPayClickTurboPaymentMethodChange}
                        onTurboPayBtnImpressionEvent={handleTurboPayBtnImpression}
                        onTurboPayImpressionEvent={handleTurboCheckoutImpression}
                        paymentAlertConfig={{
                            hideCloseIcon: false,
                            hideOnOverlayClick: true,
                            primaryAction: { onClick: handlePaymentAlertPrimaryAction },
                            onClose: onDismissPaymentAlert,
                        }}
                    />
                )}
                {(!_isTurboEnabled || isTurboPayWidgetHidden) && (
                    <>
                        <ProceedCTA data-testid={"ProceedToPayDoCart"} onClick={onProceedToPay}>
                            <ButtonTextContainer>
                                <StyledText variant={"V2.subheading1.neutral.bold"} color={"background_Primary"}>
                                    {proceedToPayText}
                                </StyledText>
                            </ButtonTextContainer>
                        </ProceedCTA>
                    </>
                )}
            </CTAContainer>
        );
    }, [
        hasCartError,
        reviewedCartMeta,
        _isTurboEnabled,
        isTurboPayWidgetHidden,
        uniqueIdForCart,
        proceedToPayText,
        onProceedToPay,
        onErrorTurboPay,
        onPayClickTurboCheckout,
        onPayClickTurboPaymentMethodChange,
        handleTurboCheckoutImpression,
        handleTurboPayBtnImpression,
        onErrorCtaAction,
        unavailableItems,
        handlePaymentAlertPrimaryAction,
        onDismissPaymentAlert,
    ]);

    return (
        <FooterContainer ref={footerRef} showBackground={hasCartError}>
            {renderContent()}
        </FooterContainer>
    );
};

export default memo(CartFooter);
